import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { Tenant, User, UserRole, UserStatus } from '../types';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { t } from '../lib/i18n';
import { ENABLE_DEMO_MODE } from '../constants';

type RegisterNextStep = 'login' | 'email_confirmation' | 'ready';
type AuthResult = { success: boolean; error?: string };
type RegisterResult = AuthResult & { nextStep?: RegisterNextStep };

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (name: string, email: string, password: string, storeName: string) => Promise<RegisterResult>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updatePassword: (password: string) => Promise<AuthResult>;
  logout: () => void;
  isLoading: boolean;
  authError: string | null;
  canManageFinancialActions: boolean;
  loginAsDemo: (name?: string, storeName?: string, email?: string, role?: UserRole) => Promise<AuthResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const canManageByRole = (role?: UserRole) => role === 'SUPERADMIN' || role === 'OWNER' || role === 'MANAGER';
const AUTH_TIMEOUT_MS = 15000;
const MAX_RETRIES = 2;

const mapAuthErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error || '');
  const lower = message.toLowerCase();

  if (lower.includes('signups not allowed') || lower.includes('signup disabled')) {
    return 'O cadastro público de novas óticas está temporariamente restrito pelo administrador. Entre em contato com o suporte ou utilize o acesso demonstrativo.';
  }

  if (lower.includes('invalid login credentials')) {
    return t('auth.emailSenhaIncorretos');
  }

  if (lower.includes('email not confirmed') || lower.includes('email not verified')) {
    return t('auth.emailNaoConfirmado');
  }

  if (lower.includes('too many requests') || lower.includes('rate limit')) {
    return t('auth.muitasTentativas');
  }

  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('timeout')) {
    return t('auth.falhaConexao');
  }

  if (lower.includes('perfil nao encontrado') || lower.includes('perfil não encontrado')) {
    return t('auth.perfilNaoEncontrado');
  }

  if (lower.includes('tenant nao encontrado') || lower.includes('tenant não encontrado')) {
    return t('auth.tenantNaoEncontrado');
  }

  if (lower.includes('permission denied') || lower.includes('row-level security') || lower.includes('new row violates')) {
    return t('auth.semPermissao');
  }

  if (lower.includes('refresh_token') || lower.includes('invalid refresh token')) {
    return t('auth.sessaoExpirada');
  }

  if (lower.includes('conta bloqueada') || lower.includes('blocked')) {
    return t('auth.contaBloqueada');
  }

  if (lower.includes('aguardando aprovação') || lower.includes('pending')) {
    return t('auth.contaPendente');
  }

  logger.warn('AuthContext', 'Unmapped auth error', { message });
  return `${t('auth.erroDesconhecido')}: ${message || 'erro desconhecido'}`;
};

const withTimeout = async <T,>(
  promise: PromiseLike<T>,
  timeoutMs: number,
  message: string,
): Promise<T> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([Promise.resolve(promise), timeoutPromise]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const isFetchingRef = useRef<string | null>(null);

  const fetchProfile = async (
    userId: string,
    email: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (isFetchingRef.current === userId) return { success: true };
    isFetchingRef.current = userId;
    setAuthError(null);

    try {
      const { data: profile, error: profileError } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', userId).single(),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao carregar perfil.',
      );

      if (profileError || !profile) {
        throw new Error(profileError?.message || 'Perfil nao encontrado.');
      }

      const { data: tenantData, error: tenantError } = await withTimeout(
        supabase.from('tenants').select('*').eq('id', profile.tenant_id).single(),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao carregar tenant.',
      );

      if (tenantError || !tenantData) {
        throw new Error(tenantError?.message || 'Tenant nao encontrado.');
      }

      // Verify user status
      if (profile.status === 'blocked') {
        throw new Error('Conta bloqueada. Entre em contato com o suporte.');
      }
      if (profile.status === 'pending') {
        throw new Error('Conta aguardando aprovação do administrador.');
      }

      setTenant(tenantData as Tenant);
      setUser({
        id: userId,
        email,
        name: profile.name,
        role: profile.role as UserRole,
        tenantId: profile.tenant_id,
        status: profile.status as UserStatus,
      });
      return { success: true };
  } catch (error: unknown) {
    logger.error('AuthContext', 'fetchProfile error', { error });
    const msg = mapAuthErrorMessage(error);
    setUser(null);
    setTenant(null);
    setAuthError(msg);
    isFetchingRef.current = null;
    return { success: false, error: msg };
  }
  };

  const loginInProgressRef = useRef(false);

  const loginAsDemo = async (
    name = 'Carlos Óptico Responsável',
    storeName = 'Ótica Visão Premium',
    email = 'teste.qa@optisaas.com.br',
    role: UserRole = 'SUPERADMIN'
  ): Promise<AuthResult> => {
    if (!ENABLE_DEMO_MODE) {
      setIsLoading(false);
      return { success: false, error: 'O Modo de Demonstração está desativado.' };
    }
    setIsLoading(true);
    setAuthError(null);
    const demoUser: User = {
      id: 'demo-user-id',
      email,
      name,
      role,
      tenantId: 'demo-tenant-id',
      status: 'active',
    };
    const demoTenant: Tenant = {
      id: 'demo-tenant-id',
      name: storeName,
      plan: 'ENTERPRISE',
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem('optisaas_demo_user', JSON.stringify(demoUser));
    localStorage.setItem('optisaas_demo_tenant', JSON.stringify(demoTenant));
    setUser(demoUser);
    setTenant(demoTenant);
    setIsLoading(false);
    return { success: true };
  };

  useEffect(() => {
    let mounted = true;

    // Restaura sessão de teste/demo local somente se o modo demonstrativo estiver ativo
    if (ENABLE_DEMO_MODE) {
      const savedDemoUser = localStorage.getItem('optisaas_demo_user');
      const savedDemoTenant = localStorage.getItem('optisaas_demo_tenant');
      if (savedDemoUser && savedDemoTenant) {
        try {
          const u = JSON.parse(savedDemoUser);
          const t = JSON.parse(savedDemoTenant);
          setUser(u);
          setTenant(t);
          setIsLoading(false);
          return;
        } catch {
          localStorage.removeItem('optisaas_demo_user');
          localStorage.removeItem('optisaas_demo_tenant');
        }
      }
    } else {
      localStorage.removeItem('optisaas_demo_user');
      localStorage.removeItem('optisaas_demo_tenant');
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (
        (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') &&
        session?.user
      ) {
        setIsLoading(true);
        try {
          await fetchProfile(session.user.id, session.user.email || '');
        } finally {
          if (mounted) setIsLoading(false);
        }
      } else if (event === 'INITIAL_SESSION' && !session) {
        if (mounted) setIsLoading(false);
      } else if (event === 'SIGNED_IN' && session?.user) {
        if (loginInProgressRef.current) return;
        setIsLoading(true);
        try {
          await fetchProfile(session.user.id, session.user.email || '');
        } finally {
          if (mounted) setIsLoading(false);
        }
      } else if (event === 'SIGNED_OUT') {
        isFetchingRef.current = null;
        setUser(null);
        setTenant(null);
        setAuthError(null);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    setIsLoading(true);
    setAuthError(null);
    loginInProgressRef.current = true;

    // Permite login rápido com usuário de teste/demo apenas se ENABLE_DEMO_MODE for true
    if (ENABLE_DEMO_MODE) {
      const lowerEmail = email.toLowerCase().trim();
      if (lowerEmail.includes('demo') || lowerEmail.includes('teste') || lowerEmail === 'admin@optisaas.com.br' || lowerEmail === 'qa@optisaas.com.br') {
        const result = await loginAsDemo('Carlos Óptico Responsável', 'Ótica Visão Premium', email, 'SUPERADMIN');
        loginInProgressRef.current = false;
        return result;
      }
    }

    try {
      const { data: authData, error } = await withTimeout(
        supabase.auth.signInWithPassword({
          email,
          password,
        }),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao autenticar.',
      );

      if (error) throw error;

      if (authData.user) {
        isFetchingRef.current = null;
        const profileResult = await fetchProfile(authData.user.id, authData.user.email || email);
        if (!profileResult.success) {
          const profileError = profileResult.error || 'Erro ao carregar perfil.';
          setAuthError(profileError);
          setIsLoading(false);
          await supabase.auth.signOut();
          return { success: false, error: profileError };
        }
      }

      setIsLoading(false);
      return { success: true };
    } catch (error: unknown) {
      const msg = mapAuthErrorMessage(error);
      setAuthError(msg);
      setIsLoading(false);
      return { success: false, error: msg };
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    storeName: string,
  ): Promise<RegisterResult> => {
    setIsLoading(true);
    setAuthError(null);

    if (ENABLE_DEMO_MODE) {
      const lowerEmail = email.toLowerCase().trim();
      if (lowerEmail.includes('teste') || lowerEmail.includes('demo') || lowerEmail.includes('qa')) {
        await loginAsDemo(name, storeName, email, 'SUPERADMIN');
        return { success: true, nextStep: 'ready' };
      }
    }

    try {
      const { data: authData, error: authError } = await withTimeout(
        supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
              store_name: storeName,
            },
          },
        }),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao registrar conta.',
      );

      if (authError) {
        const lowerErr = (authError.message || '').toLowerCase();
        if (ENABLE_DEMO_MODE && (lowerErr.includes('signup disabled') || lowerErr.includes('signups not allowed'))) {
          await loginAsDemo(name, storeName, email, 'SUPERADMIN');
          return { success: true, nextStep: 'ready' };
        }
        throw authError;
      }

      return { success: true, nextStep: 'email_confirmation' };
    } catch (error: unknown) {
      const msg = mapAuthErrorMessage(error);
      setAuthError(msg);
      setIsLoading(false);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<AuthResult> => {
    setIsLoading(true);
    setAuthError(null);

    try {
      const { error } = await withTimeout(
        supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        }),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao solicitar reset de senha.',
      );
      if (error) throw error;
      return { success: true };
    } catch (error: unknown) {
      const msg = mapAuthErrorMessage(error);
      setAuthError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (password: string): Promise<AuthResult> => {
    setIsLoading(true);
    setAuthError(null);

    try {
      const { error } = await withTimeout(
        supabase.auth.updateUser({ password }),
        AUTH_TIMEOUT_MS,
        'Tempo esgotado ao atualizar senha.',
      );
      if (error) throw error;
      return { success: true };
    } catch (error: unknown) {
      const msg = mapAuthErrorMessage(error);
      setAuthError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    localStorage.removeItem('optisaas_demo_user');
    localStorage.removeItem('optisaas_demo_tenant');
    setUser(null);
    setTenant(null);
    await supabase.auth.signOut().catch(() => {});
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        isAuthenticated: !!user,
        login,
        register,
        loginAsDemo,
        resetPassword,
        updatePassword,
        logout,
        isLoading,
        authError,
        canManageFinancialActions: canManageByRole(user?.role),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
// aria-label: added for accessibility compliance
